import React from "react";

import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearchMinus } from "@fortawesome/pro-solid-svg-icons";

import { WithApolloV2Client } from "providers/WithApolloV2Client";
import { useDateRange } from "providers/WithDateRange";
import { useRoutes } from "utils/routes";
import { formatTimestampShort } from "utils/format";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import SuspenseBoundary from "ui/SuspenseBoundary";
import LogLines from "features/Logs/LogLines";
import LogProcessingDisabledPanel from "features/Logs/LogProcessingDisabledPanel";

import IntegrationHint from "./IntegrationHint";
import JumpToTimeBar, { useLogFocus } from "./JumpToTimeBar";
import { logStatsBucketMinutes } from "./buckets";
import { LogStatsView } from "./LogStatsView";
import { CheckpointStartingView, TempFileCreatedView } from "./LogAnalysisView";

import { useLogInsightsV2SuspenseQuery } from "./gql/LogInsights.v2.generated";
import {
  useCheckpointStartingV2SuspenseQuery,
  useTempFileCreatedV2SuspenseQuery,
} from "./gql/LogAnalysis.v2.generated";
import type { LogStatsViewServerFragment } from "./gql/LogStatsView.v2.generated";

type Props = {
  serverId: string;
  databaseId?: string;
  activeClassification?: string | null;
};

function LogInsightsContent(props: Props) {
  return (
    <WithApolloV2Client>
      <SuspenseBoundary>
        <ServerLogContext {...props} />
      </SuspenseBoundary>
    </WithApolloV2Client>
  );
}

// The one Suspense query for the server context + stats/quota panels
function ServerLogContext({
  serverId,
  databaseId,
  activeClassification,
}: Props) {
  const { from, to } = useDateRange();
  const { data } = useLogInsightsV2SuspenseQuery({
    variables: {
      serverId,
      databaseId,
      from: from.toISOString(),
      to: to.toISOString(),
    },
  });

  const server = data.server;

  return (
    <>
      {server.collector?.logCollectionDisabled && (
        <LogProcessingDisabledPanel
          disabledReasons={server.collector.logCollectionDisabledReason ?? ""}
        />
      )}
      <Panel title="Postgres Server Log">
        {server.status?.integratedLogInsights ? (
          <IntegratedLogInsights
            server={server}
            serverId={serverId}
            databaseId={databaseId}
            activeClassification={activeClassification}
          />
        ) : (
          <IntegrationHint
            serverId={serverId}
            usesLocalCollector={server.localCollector != null}
            enableLogs={server.localCollector?.enableLogs ?? false}
            systemType={server.systemType}
          />
        )}
      </Panel>
    </>
  );
}

function IntegratedLogInsights({
  server,
  serverId,
  databaseId,
  activeClassification,
}: Props & { server: LogStatsViewServerFragment }) {
  const { from, to } = useDateRange();
  const { serverLogs, databaseLogs } = useRoutes();
  // The jump bar snaps to and highlights the same buckets the stats bars are drawn at.
  const bucketMinutes = logStatsBucketMinutes(from, to);
  const { focus, setFocus } = useLogFocus(from, to);
  const effectiveTo = focus ?? to;

  return (
    <div>
      {activeClassification && (
        <PanelSection>
          <Link
            to={databaseId ? databaseLogs(databaseId) : serverLogs(serverId)}
          >
            <FontAwesomeIcon icon={faSearchMinus} /> Show all log events
          </Link>
        </PanelSection>
      )}
      <LogStatsView
        server={server}
        serverId={serverId}
        databaseId={databaseId}
        startTs={from.unix()}
        endTs={to.unix()}
        selectedClassification={activeClassification}
        highlightedTime={focus}
      />
      {/* Only W40 and S7 have an analysis view */}
      {activeClassification === "W40" && (
        <SuspenseBoundary>
          <CheckpointStartingPanel
            serverId={serverId}
            highlightedTime={focus}
          />
        </SuspenseBoundary>
      )}
      {activeClassification === "S7" && (
        <SuspenseBoundary>
          <TempFileCreatedPanel
            serverId={serverId}
            databaseId={databaseId}
            highlightedTime={focus}
          />
        </SuspenseBoundary>
      )}
      <JumpToTimePanel
        activeClassification={activeClassification}
        from={from}
        to={to}
        bucketMinutes={bucketMinutes}
        focus={focus}
        onFocusChange={setFocus}
      />
      {focus && (
        <PanelSection className="text-[#666] text-sm">
          Showing latest log entries up to {formatTimestampShort(effectiveTo)}.
        </PanelSection>
      )}
      <LogLines
        serverId={serverId}
        databaseId={databaseId}
        classification={activeClassification}
        from={from}
        to={effectiveTo}
      />
    </div>
  );
}

// The jump-to-time bar, laid out to line up with the bucket bars above it: in
// overview mode it sits in the same grid as the category bars; with a
// classification selected it spans the full width like the single detail bar.
function JumpToTimePanel({
  activeClassification,
  from,
  to,
  bucketMinutes,
  focus,
  onFocusChange,
}: {
  activeClassification?: string | null;
  from: moment.Moment;
  to: moment.Moment;
  bucketMinutes: number;
  focus: moment.Moment | null;
  onFocusChange: (focus: moment.Moment | null) => void;
}) {
  const bar = (
    <JumpToTimeBar
      from={from}
      to={to}
      bucketMinutes={bucketMinutes}
      focus={focus}
      onFocusChange={onFocusChange}
    />
  );
  if (activeClassification) {
    return (
      <PanelSection>
        <div className="font-semibold mb-1">Jump to time:</div>
        <table className="w-full">
          <tbody>
            <tr>
              <td>{bar}</td>
            </tr>
          </tbody>
        </table>
      </PanelSection>
    );
  }
  return (
    <div className="py-[5px] px-[8px] grid grid-cols-[180px_1fr] items-center">
      <div className="font-semibold">Jump to time:</div>
      {bar}
    </div>
  );
}

function CheckpointStartingPanel({
  serverId,
  highlightedTime,
}: {
  serverId: string;
  highlightedTime: moment.Moment | null;
}) {
  const { from, to } = useDateRange();
  const { data } = useCheckpointStartingV2SuspenseQuery({
    variables: { serverId, from: from.toISOString(), to: to.toISOString() },
  });
  return (
    <CheckpointStartingView
      server={data.server}
      serverId={serverId}
      startTs={from.unix()}
      endTs={to.unix()}
      highlightedTime={highlightedTime}
    />
  );
}

function TempFileCreatedPanel({
  serverId,
  databaseId,
  highlightedTime,
}: {
  serverId: string;
  databaseId?: string;
  highlightedTime: moment.Moment | null;
}) {
  const { from, to } = useDateRange();
  const { data } = useTempFileCreatedV2SuspenseQuery({
    variables: {
      serverId,
      databaseId,
      from: from.toISOString(),
      to: to.toISOString(),
    },
  });
  return (
    <TempFileCreatedView
      server={data.server}
      serverId={serverId}
      startTs={from.unix()}
      endTs={to.unix()}
      highlightedTime={highlightedTime}
    />
  );
}

export default LogInsightsContent;
